import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Waves, Volleyball, Trees } from "lucide-react";
import { AMENITY_IMAGES, PARK_PHOTOS, POOL_PHOTOS, TENNIS_PHOTOS, DRONE_PHOTO } from "../data/apartments";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const ICONS = [Waves, Volleyball, Trees];
const IMAGES = [AMENITY_IMAGES.pool, AMENITY_IMAGES.tennis, PARK_PHOTOS[0]];
const GALLERIES = [POOL_PHOTOS, TENNIS_PHOTOS, PARK_PHOTOS];

const AmenityImage = ({ title, image, gallery, rounded, index }) => {
  const shots = [image, ...gallery];
  const [current, setCurrent] = useState(image);
  return (
    <div>
      <div className={`overflow-hidden border border-[#C29B62]/20 ${rounded}`}>
        <img
          src={current}
          alt={title}
          loading="lazy"
          data-testid={`amenity-image-${index}`}
          className="w-full h-72 lg:h-96 object-cover hover:scale-105 transition-transform duration-700"
        />
      </div>
      {shots.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`amenity-gallery-${index}`}>
          {shots.map((src, i) => (
            <button
              key={i}
              type="button"
              data-testid={`amenity-thumb-${index}-${i}`}
              onClick={() => setCurrent(src)}
              className={`overflow-hidden border transition-colors ${
                current === src ? "border-[#1C3829]" : "border-[#C29B62]/25 hover:border-[#C29B62]"
              }`}
            >
              <img src={src} alt={`${title} ${i + 1}`} loading="lazy" className="w-14 h-10 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Amenities = ({ t }) => {
  const bannerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: bannerRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="servizi" data-testid="amenities-section" className="py-24 lg:py-32 bg-[#EBE7DF]/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.35em] text-[#C29B62]">{t.amenities.overline}</p>
          <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight text-[#1C3829]">{t.amenities.title}</h2>
          <p className="mt-6 text-base text-[#4A4A4A] font-light leading-relaxed">{t.amenities.sub}</p>
        </motion.div>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          ref={bannerRef}
          className="mt-14 overflow-hidden h-[22rem] lg:h-[30rem] border border-[#C29B62]/20"
          data-testid="amenities-drone-banner"
        >
          <motion.img
            src={DRONE_PHOTO}
            alt="Villa Navagero Erizzo e il parco visti dall'alto"
            style={{ y }}
            className="w-full h-[120%] object-cover"
            loading="lazy"
          />
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
          {t.amenities.items.map((item, i) => {
            const Icon = ICONS[i];
            return (
              <motion.article
                key={i}
                data-testid={`amenity-card-${i}`}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.12 }}
                className={i === 1 ? "md:mt-14" : ""}
              >
                <AmenityImage
                  title={item.title}
                  image={IMAGES[i]}
                  gallery={GALLERIES[i]}
                  rounded={i === 1 ? "rounded-t-[10rem]" : "rounded-none"}
                  index={i}
                />
                <div className="mt-6 flex items-center gap-3">
                  <Icon className="w-5 h-5 text-[#C29B62]" />
                  <h3 className="text-2xl font-light tracking-tight text-[#1A1A1A]">{item.title}</h3>
                </div>
                <p className="mt-3 text-base text-[#4A4A4A] font-light leading-relaxed">{item.text}</p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
